import { yupResolver } from '@hookform/resolvers/yup';
import { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import TopHeader from '../../components/layouts/TopHeader';
import Button from '../../components/ui/Button';
import CustomInput from '../../components/ui/CustomInput';
import CustomRadioWithTooltip from '../../components/ui/CustomRadioWithTooltip';
import CustomReactSelect from '../../components/ui/CustomReactSelect';
import { ApiConstants } from '../../constants/ApiConstants';
import { ApiTypes, LOCATION_TYPES } from '../../constants/Constants';
import { Routes } from '../../constants/RouteConstants';
import ValidationMessage from '../../constants/ValidationMessage';
import authContext from '../../context/authContext';
import useMakeRequest from '../../utils/ApiHelper';
import PageLayout from '../../components/ui/PageLayout';


interface householdInputs {
    name: string;
    country: number,
    state: number,
    locality: object | null,
    billing_date: any
};

const HouseHoldValidation: any = yup.object().shape({
    name: yup.string().required(ValidationMessage.Field("a household name"))
        .min(3, ValidationMessage?.MinLength("The household name", 3))
        .max(25, ValidationMessage?.MaxLength("The household name", 25)),

    country: yup.number().required(ValidationMessage.Field("a country")),
    state: yup.number().required(ValidationMessage.Field("a state")),
    locality: yup.object().required(ValidationMessage.Field("a locality")),
});

const AddHousehold = () => {

    const navigate = useNavigate()
    const { state } = useLocation();
    const AddHousehold = useMakeRequest()
    const profile: any = useContext(authContext);
    const countries = useMakeRequest();
    const localities = useMakeRequest();
    const states = useMakeRequest();
    const [selected, setSelected] = useState('task_based');

    const billinDateOptions = Array.from({ length: 31 }, (_, i) => ({
        label: (i + 1).toString(),
        value: i + 1,
    }));

    const { register, watch, setValue, formState: { errors }, handleSubmit, trigger } = useForm<householdInputs>({
        mode: 'onChange',
        resolver: yupResolver(HouseHoldValidation) as any,
        defaultValues: { billing_date: 1 }
    });

    const onSubmit = (data: any) => {
        const payload = {
            ...data,
            locality: data?.locality?.value,
            task_pricing_type: selected

        }
        toast.promise(
            AddHousehold.makeRequest({ payload, url: ApiConstants?.Addhousehold }),
            {
                loading: 'Processing...',
                success: (data: any) => data.message,
                error: (error: any) => error.message,
            }
        );

    }


    useEffect(() => {
        const fetch = async () => {
            await profile.makeRequest({ url: ApiConstants?.Profile, type: ApiTypes?.GET });
            navigate(Routes?.ManageHousehold)
        }
        if (AddHousehold?.status) {
            fetch();
        }
    }, [AddHousehold?.status])

    useEffect(() => {
        countries.makeRequest({ url: ApiConstants?.LocationGet, payload: { type: LOCATION_TYPES?.COUNTRY, showAll: true } })
    }, [])

    useEffect(() => {
        if (countries?.status) {
            setValue('country', countries?.data?.data[0]?.value)
            trigger('country')
            states.makeRequest({ url: ApiConstants?.LocationGet, payload: { type: LOCATION_TYPES?.STATE, list_id: countries?.data?.data[0]?.value, showAll: true } })
        }
    }, [countries?.status])

    useEffect(() => {
        if (profile?.data) {
            if (profile?.data?.state_detail) {
                setValue('state', profile?.data?.state_detail?.value);
                trigger('state');
            }

            if (profile?.data?.locality_detail) {
                setValue('locality', profile?.data?.locality_detail);
                trigger('locality');
            }
        }
    }, [profile?.data]);

    return (
        <PageLayout headerName="Add Household" onlyTitle={false}>
            <form onSubmit={handleSubmit(onSubmit)} className='page-padding-x'>
                <div className="auth-form">
                    <div className="auth-form-fields">
                        <CustomInput
                            label="Household Name"
                            type="text"
                            placeholder="Enter name"
                            required
                            {...register('name')}
                            error={errors.name?.message}
                        />

                        <CustomReactSelect
                            label="Country"
                            required
                            options={countries?.data?.data}
                            value={{
                                value: countries?.data?.data?.[0]?.value,
                                label: countries?.data?.data?.[0]?.label
                            }}
                            placeholder="Please select household name"
                            onChange={(selectedOption: any) => {
                                console.log("selectedOption: ", selectedOption)
                                setValue("country", selectedOption ? selectedOption?.value : "");
                                trigger("country")
                                states.makeRequest({ url: ApiConstants?.LocationGet, payload: { type: LOCATION_TYPES?.COUNTRY, list_id: selectedOption?.value, showAll: true } })
                            }}
                            isLoading={countries?.loading}
                            error={errors}
                            errorName="country"
                            closeMenuOnScroll={true}
                            closeMenuOnSelect={true}
                        />

                        <CustomReactSelect
                            label="State"
                            required
                            options={states?.data?.data}
                            defaultValue={profile?.data?.state_detail}
                            placeholder="Select state"
                            onChange={(selectedOption: any) => {
                                setValue("state", selectedOption ? selectedOption?.value : "");
                                setValue('locality', null)
                                trigger("state")
                                localities.makeRequest({ url: ApiConstants?.LocationGet, payload: { type: 3, list_id: selectedOption?.value, showAll: true } })
                            }}
                            isLoading={states?.loading}
                            error={errors}
                            errorName="state"
                            closeMenuOnScroll={true}
                            closeMenuOnSelect={true}
                            menuPosition="fixed"
                            menuShouldScrollIntoView={false}
                            menuPortalTarget={document.body}
                            styles={{
                                menuPortal: base => ({ ...base, zIndex: 9999 })
                            }}
                        />
                        <CustomReactSelect
                            label="Locality"
                            required
                            options={localities?.data?.data}
                            defaultValue={profile?.data?.locality_detail}
                            value={watch('locality') && watch('locality')}
                            placeholder="Select locality"
                            onChange={(selectedOption: any) => {
                                setValue("locality", selectedOption ?? null);
                                trigger("locality")
                            }}
                            isLoading={localities?.loading}
                            closeMenuOnScroll={true}
                            closeMenuOnSelect={true}
                            menuPosition="fixed"
                            menuShouldScrollIntoView={false}
                            menuPortalTarget={document.body}
                            styles={{
                                menuPortal: base => ({ ...base, zIndex: 9999 })
                            }}
                            error={errors}
                            errorName="locality"
                        />

                        <CustomReactSelect
                            label="Budget Period Start Date"
                            defaultValue={billinDateOptions[0]}
                            options={billinDateOptions as any}
                            placeholder="Select Billing Cycle Date"
                            onChange={(selectedOption: any) => {
                                setValue("billing_date", selectedOption?.value);
                                trigger("billing_date")
                            }}
                            tooltip={{
                                content: 'Select the day of the month when your household budget period starts. This date will be used to track and manage your household expenses effectively.',
                                id: 'billing_date_tooltip',
                                place: 'bottom'
                            }}
                            closeMenuOnScroll={true}
                            closeMenuOnSelect={true}
                            menuPosition="fixed"
                            menuShouldScrollIntoView={false}
                            menuPortalTarget={document.body}
                            styles={{
                                menuPortal: base => ({ ...base, zIndex: 9999 })
                            }}
                        />

                        {[
                            {
                                title: "Task-based pricing",
                                type: "task_based",
                                tooltipText:
                                    "Task-based pricing",
                            },
                            {
                                title: "Task-neutral pricing",
                                type: "task_neutral",
                                tooltipText:
                                    "Task-neutral pricing",
                            },
                        ].map((pricing) => (
                            <CustomRadioWithTooltip
                                id={pricing?.type}
                                name="divisionRadio"
                                value={pricing?.type}
                                label={pricing?.title}
                                tooltipText={pricing?.tooltipText}
                                checked={pricing?.type === selected}
                                onChange={setSelected}
                            />
                        ))}
                    </div>

                    <div className="page-padding-x">
                        <Button text='Continue' type='submit' className='com-btn my-5 w-100' loading={AddHousehold?.loading}>Continue</Button>
                    </div>
                </div>

            </form>
        </PageLayout>
    )

}

export default AddHousehold